import { Highlight, themes } from "prism-react-renderer";
import type { CSSProperties } from "react";

/** Input of CodeView: the text, its Prism language, light or dark colors and the region name. */
export type CodeViewProps = {
  code: string;
  /** Prism language name; null or unknown shows plain text. */
  language: string | null;
  colorScheme: "light" | "dark";
  /** Accessible name of the scrollable code region. */
  label: string;
};

// Inline styles: the app's Tailwind build does not scan this package.
const MONO =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';
const regionStyle: CSSProperties = { height: "100%", overflow: "auto" };
const preStyle: CSSProperties = {
  margin: 0,
  padding: "12px 0",
  minWidth: "100%",
  width: "max-content",
  fontFamily: MONO,
  fontSize: 13,
  lineHeight: 1.6,
  whiteSpace: "pre",
  tabSize: 2,
};
const lineStyle: CSSProperties = { display: "flex" };
const numberStyle: CSSProperties = {
  position: "sticky",
  insetInlineStart: 0,
  display: "inline-block",
  flexShrink: 0,
  width: "3.25rem",
  paddingInlineEnd: 16,
  textAlign: "end",
  opacity: 0.5,
  userSelect: "none",
};
const textStyle: CSSProperties = { paddingInlineEnd: 16 };

/**
 * Read-only source view with syntax highlighting and line numbers (PIV-36).
 * prism-react-renderer turns the code into React text nodes, so nothing in it is
 * ever parsed as HTML. Line numbers are not selectable, so copying takes only code.
 */
export function CodeView({ code, language, colorScheme, label }: CodeViewProps) {
  const theme = colorScheme === "dark" ? themes.oneDark : themes.oneLight;
  const lines = code.endsWith("\n") ? code.slice(0, -1) : code;
  return (
    <Highlight theme={theme} code={lines} language={language || "text"}>
      {({ style, tokens, getTokenProps }) => (
        <section
          aria-label={label}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be keyboard-focusable (WCAG 2.1.1).
          tabIndex={0}
          style={{ ...regionStyle, backgroundColor: style.backgroundColor, color: style.color }}
        >
          <pre data-testid="code-view" data-language={language ?? "text"} style={preStyle}>
            <code>
              {tokens.map((line, lineIndex) => (
                <div key={lineIndex} style={lineStyle}>
                  <span
                    aria-hidden="true"
                    style={{ ...numberStyle, backgroundColor: style.backgroundColor }}
                  >
                    {lineIndex + 1}
                  </span>
                  <span style={textStyle}>
                    {line.map((token, tokenIndex) => {
                      // Only the colour: Prism's class names (e.g. "table") collide with
                      // utility classes of the app and would change the layout.
                      const { style: tokenStyle, children } = getTokenProps({ token });
                      return (
                        <span key={tokenIndex} style={tokenStyle}>
                          {children}
                        </span>
                      );
                    })}
                  </span>
                </div>
              ))}
            </code>
          </pre>
        </section>
      )}
    </Highlight>
  );
}
